import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchPlayerMatches,
  fetchPlayerRatingHistory,
  type MatchEntry,
  type RankingItem,
  type RatingHistoryEntry,
} from "../utils/homeApi";
import { determineMatchResult } from "../utils/matchResultHelper";
import { buildRatingChartPoints } from "../utils/ratingChartPoints";
import { byNewestFirst, byOldestFirst } from "../utils/match-order";
import RatingChangeCell from "./rating-change-cell";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface DetailedDoublesMatch {
  tournamentNumber: number;
  tournamentFile: string;
  tournamentName?: string;
  // Chronology lives here — file numbers are not time-ordered. Dropping these two
  // fields made every match list fall back to sorting by tournament number.
  occurredAt?: string;
  matchId?: number;
  dataType: string;
  matchIndex: number;
  team1: string[];
  team2: string[];
  team1Ids?: number[];
  team2Ids?: number[];
  score: number;
  setScores?: string[];
  matchResult?: string;
  fractional?: boolean;
  // Waga, którą księga zapłaciła za ten mecz; null = mecz sprzed jej zapisywania.
  ratingMultiplier?: number | null;
  winner: 1 | 2 | "draw";
  isSingleMatch: boolean;
  team1RatingBefore: number;
  team1RatingAfter: number;
  team1RatingChange: number;
  team2RatingBefore: number;
  team2RatingAfter: number;
  team2RatingChange: number;
  expectedScore1?: number;
  expectedScore2?: number;
  playerRatingChanges: {
    [playerName: string]: {
      ratingBefore: number;
      ratingAfter: number;
      ratingChange: number;
    };
  };
}

interface PartnerStats {
  matches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
}

function getRatingBadgeClass(rating: number): string {
  if (rating >= 2500) {
    return "very-high";
  }

  if (rating >= 2200) {
    return "high";
  }

  if (rating >= 1900) {
    return "medium";
  }

  if (rating >= 1600) {
    return "low";
  }

  return "very-low";
}

interface Props {
  playerId: number;
  playerName: string;
  doubles: RankingItem;
}

const DoublesView: React.FC<Props> = ({ playerId, playerName, doubles }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<DetailedDoublesMatch[]>([]);
  const [ratingHistory, setRatingHistory] = useState<RatingHistoryEntry[]>([]);
  const [nameToId, setNameToId] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState<"overview" | "matches" | "partners">("overview");
  const [expandedPartners, setExpandedPartners] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    fetchPlayerRatingHistory(playerId, "DOUBLES")
      .then((res) => {
        if (alive) {
          setRatingHistory(res.items);
        }
      })
      .catch((error) => {
        console.error("Error loading rating history:", error);
      });

    return () => {
      alive = false;
    };
  }, [playerId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPlayerMatches(playerId, { limit: 2000, kind: "doubles" })
      .then((res) => {
        if (!alive) {
          return;
        }

        const adapted: DetailedDoublesMatch[] = res.items.map((m: MatchEntry, idx: number) => ({
          tournamentNumber: m.tournamentNumber,
          tournamentFile: m.tournamentFile ?? "",
          tournamentName: m.tournamentName,
          occurredAt: m.occurredAt,
          matchId: m.matchId,
          dataType: m.dataType,
          matchIndex: idx,
          team1: m.team1 ?? [],
          team2: m.team2 ?? [],
          team1Ids: m.team1Ids ?? undefined,
          team2Ids: m.team2Ids ?? undefined,
          score: typeof m.score === "number" ? m.score : 0,
          setScores: m.setScores,
          matchResult: m.matchResult,
          fractional: m.fractional ?? false,
          ratingMultiplier: m.ratingMultiplier ?? null,
          winner: (m.winner === 1 || m.winner === 2 ? m.winner : "draw") as 1 | 2 | "draw",
          isSingleMatch: m.isSingleMatch,
          team1RatingBefore: m.team1RatingBefore ?? 0,
          team1RatingAfter: m.team1RatingAfter ?? 0,
          team1RatingChange: m.team1RatingChange ?? 0,
          team2RatingBefore: m.team2RatingBefore ?? 0,
          team2RatingAfter: m.team2RatingAfter ?? 0,
          team2RatingChange: m.team2RatingChange ?? 0,
          expectedScore1: (m as unknown as { expectedScore1?: number }).expectedScore1,
          expectedScore2: (m as unknown as { expectedScore2?: number }).expectedScore2,
          playerRatingChanges:
            ((m as unknown as { playerRatingChanges?: DetailedDoublesMatch["playerRatingChanges"] }).playerRatingChanges) ?? {},
        }));

        setMatches(adapted);

        const idMap = new Map<string, number>();
        adapted.forEach((m) => {
          (m.team1 ?? []).forEach((name, i) => {
            const id = m.team1Ids?.[i];
            if (typeof id === "number" && !idMap.has(name)) {
              idMap.set(name, id);
            }
          });
          (m.team2 ?? []).forEach((name, i) => {
            const id = m.team2Ids?.[i];
            if (typeof id === "number" && !idMap.has(name)) {
              idMap.set(name, id);
            }
          });
        });

        if (playerName) {
          idMap.set(playerName, playerId);
        }

        setNameToId(idMap);
      })
      .catch((error) => {
        console.error("Error loading doubles matches:", error);
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [playerId, playerName]);

  const isOnTeam1 = (match: DetailedDoublesMatch): boolean => {
    if (match.team1Ids && match.team1Ids.includes(playerId)) {
      return true;
    }

    if (match.team2Ids && match.team2Ids.includes(playerId)) {
      return false;
    }

    // Legacy fallback for matches without ID arrays — name-based.
    return match.team1.includes(playerName);
  };

  const isPlayerInMatch = (match: DetailedDoublesMatch): boolean => {
    if (match.team1Ids?.includes(playerId) || match.team2Ids?.includes(playerId)) {
      return true;
    }

    if (!match.team1Ids && !match.team2Ids) {
      return match.team1.includes(playerName) || match.team2.includes(playerName);
    }

    return false;
  };

  const partnerOf = (match: DetailedDoublesMatch, onTeam1: boolean): string => {
    const team = onTeam1 ? match.team1 : match.team2;
    const ids = onTeam1 ? match.team1Ids : match.team2Ids;

    if (ids) {
      const idx = team.findIndex((_, i) => ids[i] !== playerId);
      if (idx >= 0) {
        return team[idx] || "Nieznany";
      }
    }

    return team.find((p) => p !== playerName) || "Nieznany";
  };

  const partners: Record<string, PartnerStats> = {};
  let totalMatches = 0;
  let wins = 0;

  matches.forEach((match) => {
    if (!isPlayerInMatch(match)) {
      return;
    }

    totalMatches++;

    const onTeam1 = isOnTeam1(match);
    const partnerName = partnerOf(match, onTeam1);

    if (!partners[partnerName]) {
      partners[partnerName] = { matches: 0, wins: 0, losses: 0, draws: 0, winRate: 0 };
    }

    partners[partnerName].matches++;

    const result = determineMatchResult(match, onTeam1);
    if (result === "W") {
      wins++;
      partners[partnerName].wins++;
    } else if (result === "L") {
      partners[partnerName].losses++;
    } else {
      partners[partnerName].draws++;
    }
  });

  Object.values(partners).forEach((p) => {
    p.winRate = p.matches > 0 ? (p.wins / p.matches) * 100 : 0;
  });

  const playerMatches = matches.filter(isPlayerInMatch);

  const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Aktualny rating (debel)</h3>
          <p className="value">
            <span className={`rating-badge ${getRatingBadgeClass(doubles.rating)}`}>
              {doubles.rating}
            </span>
          </p>
        </div>
        <div className="stat-card">
          <h3>Łącznie meczów</h3>
          <p className="value">{doubles.gamesPlayed}</p>
        </div>
        <div className="stat-card">
          <h3>% Wygranych</h3>
          <p
            className="value"
            title={
              doubles.gamesPlayed > 0
                ? `Wygrane ${((doubles.gamesWon / doubles.gamesPlayed) * 100).toFixed(1)}% · Remisy ${((doubles.gamesDrawn / doubles.gamesPlayed) * 100).toFixed(1)}% · Przegrane ${((doubles.gamesLost / doubles.gamesPlayed) * 100).toFixed(1)}%`
                : "Brak meczów"
            }
          >
            {doubles.gamesPlayed > 0
              ? `${((doubles.gamesWon / doubles.gamesPlayed) * 100).toFixed(1)}%`
              : "—"}
          </p>
        </div>
        <div className="stat-card">
          <h3>Turnieje</h3>
          <p className="value">{doubles.tournamentsPlayed}</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <button
          onClick={() => setSubTab("overview")}
          className={subTab === "overview" ? "btn-primary" : "btn-secondary"}
        >
          Przegląd
        </button>
        <button
          onClick={() => setSubTab("matches")}
          className={subTab === "matches" ? "btn-primary" : "btn-secondary"}
        >
          Historia meczów{loading ? " …" : ` (${playerMatches.length})`}
        </button>
        <button
          onClick={() => setSubTab("partners")}
          className={subTab === "partners" ? "btn-primary" : "btn-secondary"}
        >
          Partnerzy{loading ? "" : ` (${Object.keys(partners).length})`}
        </button>
      </div>

      {subTab === "overview" && (
        <div>
          <h3>Statystyki debla</h3>
          <div className="table-container">
            <table>
              <tbody>
                <tr>
                  <td>
                    <strong>Aktualny rating:</strong>
                  </td>
                  <td>
                    <span className={`rating-badge ${getRatingBadgeClass(doubles.rating)}`}>
                      {doubles.rating}
                    </span>
                  </td>
                  <td>
                    <strong>Początkowy rating:</strong>
                  </td>
                  <td>{doubles.startingRating ?? "—"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Zmiana ratingu w ostatnim turnieju:</strong>
                  </td>
                  <td style={{ color: doubles.ratingDifference >= 0 ? "green" : "red" }}>
                    {doubles.ratingDifference >= 0 ? "+" : ""}
                    {doubles.ratingDifference}
                  </td>
                  <td>
                    <strong>Rozegrane mecze:</strong>
                  </td>
                  <td>{doubles.gamesPlayed}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Wygrane / Przegrane / Remisy:</strong>
                  </td>
                  <td>
                    {doubles.gamesWon} / {doubles.gamesLost} / {doubles.gamesDrawn}
                  </td>
                  <td>
                    <strong>% Wygranych:</strong>
                  </td>
                  <td
                    title={
                      doubles.gamesPlayed > 0
                        ? `Wygrane ${((doubles.gamesWon / doubles.gamesPlayed) * 100).toFixed(1)}% · Remisy ${((doubles.gamesDrawn / doubles.gamesPlayed) * 100).toFixed(1)}% · Przegrane ${((doubles.gamesLost / doubles.gamesPlayed) * 100).toFixed(1)}%`
                        : "Brak meczów"
                    }
                  >
                    {doubles.gamesPlayed > 0
                      ? `${((doubles.gamesWon / doubles.gamesPlayed) * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Pierwszy turniej:</strong>
                  </td>
                  <td>
                    {doubles.firstTournament != null ? (
                      <Link to={`/tournament/${doubles.firstTournament}`} className="player-link">
                        #{doubles.firstTournament}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <strong>Ostatni turniej:</strong>
                  </td>
                  <td>
                    {doubles.latestTournament != null ? (
                      <Link to={`/tournament/${doubles.latestTournament}`} className="player-link">
                        #{doubles.latestTournament}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {ratingHistory.length > 0 && (
            <DoublesRatingHistoryChart
              history={ratingHistory}
              currentRating={doubles.rating}
              matchesFallback={matches}
              onPointClick={(tournamentNumber) => {
                navigate(`/tournament/${tournamentNumber}`);
              }}
            />
          )}

          <div style={{ marginTop: 12, color: "#666", fontSize: "0.9rem" }}>
            Łącznie: <strong>{totalMatches}</strong> · Win rate <strong>{winRate.toFixed(1)}%</strong>
          </div>
        </div>
      )}

      {subTab === "matches" && (
        <div>
          <h3>Historia meczów (debel)</h3>
          {loading ? (
            <p>Wczytywanie meczów…</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Turniej</th>
                    <th>Partner</th>
                    <th>Przeciwnicy</th>
                    <th title="Wyniki setów">Sety</th>
                    <th title="Wartość wyniku">Wartość</th>
                    <th title="Oczekiwany wynik">Oczekiwany</th>
                    <th>Rezultat</th>
                    <th title="Rating zawodnika przed meczem">Rat. przed</th>
                    <th title="Rating zawodnika po meczu">Rat. po</th>
                    <th title="Zmiana ratingu i waga, z jaką mecz policzył się do rankingu">Zmiana</th>
                    <th>Rating pary</th>
                    <th>Rating przec.</th>
                  </tr>
                </thead>
                <tbody>
                  {playerMatches
                    .slice()
                    .sort((a, b) => {
                      const byTime = byNewestFirst(a, b);

                      if (byTime !== 0) {
                        return byTime;
                      }

                      return a.matchIndex - b.matchIndex;
                    })
                    .map((match, index) => {
                      const isTeam1 = isOnTeam1(match);
                      const partnerName = partnerOf(match, isTeam1);
                      const opponentNames = isTeam1 ? match.team2 : match.team1;
                      const playerRatingData =
                        match.playerRatingChanges[playerId] ||
                        match.playerRatingChanges[String(playerId)] ||
                        match.playerRatingChanges[playerName];
                      const ratingBefore =
                        playerRatingData?.ratingBefore ||
                        (isTeam1 ? match.team1RatingBefore : match.team2RatingBefore);
                      const ratingAfter =
                        playerRatingData?.ratingAfter ||
                        (isTeam1 ? match.team1RatingAfter : match.team2RatingAfter);
                      const ratingChange =
                        playerRatingData?.ratingChange ||
                        (isTeam1 ? match.team1RatingChange : match.team2RatingChange);

                      const playerMatchResult = determineMatchResult(match, isTeam1);
                      const playerScore = isTeam1 ? match.score : 1 - match.score;
                      const expectedScore = isTeam1 ? match.expectedScore1 : match.expectedScore2;

                      return (
                        <tr key={index}>
                          <td>
                            <Link to={`/tournament/${match.tournamentNumber}`} className="player-link">
                              #{match.tournamentNumber}
                            </Link>
                            {match.fractional ? (
                              <span
                                title="Klasyfikacja ułamkowa (Pkt/set 3/2/1/0)"
                                style={{
                                  marginLeft: "0.4rem",
                                  padding: "0.1rem 0.4rem",
                                  borderRadius: "10px",
                                  background: "#0d6efd",
                                  color: "white",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                }}
                              >
                                Pkt
                              </span>
                            ) : null}
                          </td>
                          <td>
                            {nameToId.get(partnerName) != null ? (
                              <Link
                                to={`/player/${nameToId.get(partnerName)}?tab=debel`}
                                className="player-link"
                              >
                                {partnerName}
                              </Link>
                            ) : (
                              <span>{partnerName}</span>
                            )}
                          </td>
                          <td>
                            {opponentNames.map((opponent, idx) => (
                              <React.Fragment key={opponent}>
                                {nameToId.get(opponent) != null ? (
                                  <Link
                                    to={`/player/${nameToId.get(opponent)}?tab=debel`}
                                    className="player-link"
                                  >
                                    {opponent}
                                  </Link>
                                ) : (
                                  <span>{opponent}</span>
                                )}
                                {idx < opponentNames.length - 1 && " / "}
                              </React.Fragment>
                            ))}
                          </td>
                          <td>
                            {match.setScores && match.setScores.length > 0 ? (
                              <span>
                                <strong>
                                  {isTeam1
                                    ? match.matchResult || "N/A"
                                    : (() => {
                                        const [s1, s2] = (match.matchResult || "0-0").split("-");

                                        return `${s2}-${s1}`;
                                      })()}
                                </strong>{" "}
                                <span style={{ fontSize: "0.9rem", color: "#666" }}>
                                  (
                                  {isTeam1
                                    ? match.setScores.join(" ")
                                    : match.setScores
                                        .map((score) => {
                                          const [a, b] = score.split(":");

                                          return `${b}:${a}`;
                                        })
                                        .join(" ")}
                                  )
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: "#999", fontStyle: "italic" }}>
                                Brak wyników setów
                              </span>
                            )}
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#6c757d",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {playerScore.toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#17a2b8",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {expectedScore ? expectedScore.toFixed(2) : "N/A"}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                fontWeight: "bold",
                                color:
                                  playerMatchResult === "W"
                                    ? "green"
                                    : playerMatchResult === "L"
                                      ? "red"
                                      : "blue",
                              }}
                            >
                              {playerMatchResult}
                            </span>
                          </td>
                          <td>{Math.round(ratingBefore)}</td>
                          <td>{Math.round(ratingAfter)}</td>
                          <RatingChangeCell
                            ratingChange={ratingChange}
                            multiplier={match.ratingMultiplier}
                          />
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#28a745",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {isTeam1 ? match.team1RatingBefore || "N/A" : match.team2RatingBefore || "N/A"}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                borderRadius: "15px",
                                background: "#dc3545",
                                color: "white",
                                fontSize: "0.9rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {isTeam1 ? match.team2RatingBefore || "N/A" : match.team1RatingBefore || "N/A"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {subTab === "partners" && (
        <div>
          <h3>Statystyki z partnerami</h3>
          {loading ? (
            <p>Wczytywanie meczów…</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Mecze</th>
                    <th>Wygrane</th>
                    <th>Przegrane</th>
                    <th>Remisy</th>
                    <th title="Procent wygranych spotkań. Najedź na wartość żeby zobaczyć remisy/przegrane.">
                      % Wygranych
                    </th>
                    <th>Szczegóły</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(partners)
                    .sort(([, a], [, b]) => b.matches - a.matches)
                    .map(([partner, stats]) => {
                      const isExpanded = expandedPartners.has(partner);
                      const partnerMatches = playerMatches
                        .filter((m) => partnerOf(m, isOnTeam1(m)) === partner)
                        .sort((a, b) => {
                          const byTime = byNewestFirst(a, b);

                          if (byTime !== 0) {
                            return byTime;
                          }

                          return a.matchIndex - b.matchIndex;
                        });

                      return (
                        <React.Fragment key={partner}>
                          <tr>
                            <td>
                              {nameToId.get(partner) != null ? (
                                <Link
                                  to={`/player/${nameToId.get(partner)}?tab=debel`}
                                  className="player-link"
                                >
                                  {partner}
                                </Link>
                              ) : (
                                <span>{partner}</span>
                              )}
                            </td>
                            <td>{stats.matches}</td>
                            <td>{stats.wins}</td>
                            <td>{stats.losses}</td>
                            <td>{stats.draws}</td>
                            <td
                              title={
                                stats.matches > 0
                                  ? `Wygrane ${((stats.wins / stats.matches) * 100).toFixed(1)}% · Remisy ${((stats.draws / stats.matches) * 100).toFixed(1)}% · Przegrane ${((stats.losses / stats.matches) * 100).toFixed(1)}%`
                                  : "Brak meczów"
                              }
                            >
                              {stats.matches > 0
                                ? `${((stats.wins / stats.matches) * 100).toFixed(1)}%`
                                : "—"}
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedPartners);
                                  if (isExpanded) {
                                    newExpanded.delete(partner);
                                  } else {
                                    newExpanded.add(partner);
                                  }

                                  setExpandedPartners(newExpanded);
                                }}
                                className="btn-secondary"
                                style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                              >
                                {isExpanded ? "Ukryj" : "Pokaż"} szczegóły
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={10}>
                                <div
                                  style={{
                                    padding: "1rem",
                                    backgroundColor: "#f8f9fa",
                                    borderTop: "1px solid #dee2e6",
                                  }}
                                >
                                  <h4 style={{ margin: "0 0 1rem 0", color: "#495057" }}>
                                    Szczegóły meczów z {partner}
                                  </h4>
                                  <div className="table-container">
                                    <table style={{ fontSize: "0.9rem" }}>
                                      <thead>
                                        <tr>
                                          <th>Turniej</th>
                                          <th>Rezultat</th>
                                          <th>Przeciwnicy</th>
                                          <th>Wynik</th>
                                          <th>Oczekiwany</th>
                                          <th>Rat. zaw. przed</th>
                                          <th>Rat. zaw. po</th>
                                          <th>Zmiana</th>
                                          <th>Rat. pary</th>
                                          <th>Rat. przec.</th>
                                          <th>Wyniki setów</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {partnerMatches.map((match, index) => {
                                          const isTeam1 = isOnTeam1(match);
                                          const opponentNames = isTeam1 ? match.team2 : match.team1;
                                          const playerRatingData =
                                            match.playerRatingChanges[playerId] ||
                                            match.playerRatingChanges[String(playerId)] ||
                                            match.playerRatingChanges[playerName];
                                          const ratingBefore =
                                            playerRatingData?.ratingBefore ||
                                            (isTeam1 ? match.team1RatingBefore : match.team2RatingBefore);
                                          const ratingAfter =
                                            playerRatingData?.ratingAfter ||
                                            (isTeam1 ? match.team1RatingAfter : match.team2RatingAfter);
                                          const ratingChange =
                                            playerRatingData?.ratingChange ||
                                            (isTeam1 ? match.team1RatingChange : match.team2RatingChange);
                                          const result = determineMatchResult(match, isTeam1);
                                          const playerScore = isTeam1 ? match.score : 1 - match.score;
                                          const expectedScore = isTeam1
                                            ? match.expectedScore1
                                            : match.expectedScore2;

                                          return (
                                            <tr key={index}>
                                              <td>
                                                <Link
                                                  to={`/tournament/${match.tournamentNumber}`}
                                                  className="player-link"
                                                >
                                                  {match.tournamentNumber}
                                                </Link>
                                              </td>
                                              <td>
                                                <span
                                                  style={{
                                                    fontWeight: "bold",
                                                    color:
                                                      result === "W"
                                                        ? "green"
                                                        : result === "L"
                                                          ? "red"
                                                          : "blue",
                                                  }}
                                                >
                                                  {result}
                                                </span>
                                              </td>
                                              <td>
                                                {opponentNames.map((opponent, idx) => (
                                                  <React.Fragment key={opponent}>
                                                    {nameToId.get(opponent) != null ? (
                                                      <Link
                                                        to={`/player/${nameToId.get(opponent)}?tab=debel`}
                                                        className="player-link"
                                                      >
                                                        {opponent}
                                                      </Link>
                                                    ) : (
                                                      <span>{opponent}</span>
                                                    )}
                                                    {idx < opponentNames.length - 1 && " / "}
                                                  </React.Fragment>
                                                ))}
                                              </td>
                                              <td>
                                                <span
                                                  style={{
                                                    padding: "0.25rem 0.5rem",
                                                    borderRadius: "15px",
                                                    background: "#6c757d",
                                                    color: "white",
                                                    fontSize: "0.9rem",
                                                    fontFamily: "monospace",
                                                  }}
                                                >
                                                  {playerScore.toFixed(2)}
                                                </span>
                                              </td>
                                              <td>
                                                <span
                                                  style={{
                                                    padding: "0.25rem 0.5rem",
                                                    borderRadius: "15px",
                                                    background: "#17a2b8",
                                                    color: "white",
                                                    fontSize: "0.8rem",
                                                    fontFamily: "monospace",
                                                  }}
                                                >
                                                  {expectedScore ? expectedScore.toFixed(2) : "N/A"}
                                                </span>
                                              </td>
                                              <td>{ratingBefore ? Math.round(ratingBefore) : "N/A"}</td>
                                              <td>{ratingAfter ? Math.round(ratingAfter) : "N/A"}</td>
                                              <td
                                                style={{
                                                  color: (ratingChange || 0) >= 0 ? "green" : "red",
                                                }}
                                              >
                                                {(ratingChange || 0) >= 0 ? "+" : ""}
                                                {ratingChange ? Math.round(ratingChange) : "N/A"}
                                              </td>
                                              <td>
                                                <span
                                                  style={{
                                                    padding: "0.25rem 0.5rem",
                                                    borderRadius: "15px",
                                                    background: "#28a745",
                                                    color: "white",
                                                    fontSize: "0.8rem",
                                                    fontFamily: "monospace",
                                                  }}
                                                >
                                                  {isTeam1
                                                    ? match.team1RatingBefore || "N/A"
                                                    : match.team2RatingBefore || "N/A"}
                                                </span>
                                              </td>
                                              <td>
                                                <span
                                                  style={{
                                                    padding: "0.25rem 0.5rem",
                                                    borderRadius: "15px",
                                                    background: "#dc3545",
                                                    color: "white",
                                                    fontSize: "0.8rem",
                                                    fontFamily: "monospace",
                                                  }}
                                                >
                                                  {isTeam1
                                                    ? match.team2RatingBefore || "N/A"
                                                    : match.team1RatingBefore || "N/A"}
                                                </span>
                                              </td>
                                              <td>
                                                {match.setScores && match.setScores.length > 0 ? (
                                                  <div>
                                                    <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
                                                      {isTeam1
                                                        ? match.matchResult || "N/A"
                                                        : (() => {
                                                            const [s1, s2] = (
                                                              match.matchResult || "0-0"
                                                            ).split("-");

                                                            return `${s2}-${s1}`;
                                                          })()}
                                                    </div>
                                                    <div style={{ fontSize: "0.8rem", color: "#666" }}>
                                                      {isTeam1
                                                        ? match.setScores.join(" ")
                                                        : match.setScores
                                                            .map((score) => {
                                                              const [a, b] = score.split(":");

                                                              return `${b}:${a}`;
                                                            })
                                                            .join(" ")}
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <span style={{ color: "#999", fontStyle: "italic" }}>
                                                    Brak wyników setów
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ChartProps {
  history: RatingHistoryEntry[];
  currentRating: number;
  matchesFallback?: DetailedDoublesMatch[];
  onPointClick?: (tournamentNumber: number) => void;
}

const DoublesRatingHistoryChart: React.FC<ChartProps> = ({
  history,
  currentRating,
  matchesFallback,
  onPointClick,
}) => {
  // Date mode is the only reliable chronological axis — legacy tournament
  // numbers (decoded from backfill snapshot synthetic playedAt) and v2
  // projection numbers (`949.bierun.txt` etc.) share the same range but
  // aren't time-ordered, so `#1515 → #949` would look like a jump backwards.
  const [xAxisMode, setXAxisMode] = useState<"tournament" | "date">("date");
  // Match-side fallback for backfill snapshots that lack matchId (and therefore
  // tournamentNumber from the API). Sort matches chronologically and zip 1:1.
  const sortedMatches = (matchesFallback ?? [])
    .slice()
    .sort((a, b) => {
      const byTime = byOldestFirst(a, b);

      if (byTime !== 0) {
        return byTime;
      }

      return b.matchIndex - a.matchIndex;
    });

  // API already returns snapshots in ELO compute order (file-num chronology).
  // Re-sorting by playedAt would scramble them — header dates are unreliable.
  const data = buildRatingChartPoints(history, (idx) => sortedMatches[idx]?.tournamentNumber ?? null);

  if (data.length === 0) {
    return null;
  }

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getFullYear()).slice(2)}`;
  };

  // Sparse tick density — ~8 labels max across the X-axis regardless of dataset size.
  const targetTicks = 8;
  const step = Math.max(1, Math.ceil(data.length / targetTicks));

  const dateTicks: number[] = [];
  for (let i = 0; i < data.length; i += step) {
    dateTicks.push(data[i].playedAtTs);
  }
  const lastTs = data[data.length - 1].playedAtTs;
  if (dateTicks[dateTicks.length - 1] !== lastTs) {
    dateTicks.push(lastTs);
  }

  const tournamentTicks: number[] = [];
  for (let i = 0; i < data.length; i += step) {
    tournamentTicks.push(i + 1);
  }
  if (tournamentTicks[tournamentTicks.length - 1] !== data.length) {
    tournamentTicks.push(data.length);
  }

  const tournamentTickFormatter = (idx: number) => {
    const entry = data[Math.max(0, Math.min(data.length - 1, idx - 1))];
    const tn = entry?.tournamentNumber;
    return typeof tn === "number" ? `#${tn}` : `${idx}`;
  };

  return (
    <div style={{ marginTop: "30px" }}>
      <h3>
        Historia ratingu (debel){" "}
        <span style={{ color: "#666", fontWeight: 400, fontSize: "0.85rem" }}>
          · obecnie {currentRating}
        </span>
      </h3>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button
          onClick={() => setXAxisMode("tournament")}
          className={xAxisMode === "tournament" ? "btn-primary" : "btn-secondary"}
          style={{ padding: "4px 10px", fontSize: "0.85rem" }}
        >
          Numer turnieju
        </button>
        <button
          onClick={() => setXAxisMode("date")}
          className={xAxisMode === "date" ? "btn-primary" : "btn-secondary"}
          style={{ padding: "4px 10px", fontSize: "0.85rem" }}
        >
          Data
        </button>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          {xAxisMode === "date" ? (
            <XAxis
              dataKey="playedAtTs"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              ticks={dateTicks}
              tickFormatter={formatDate}
              label={{ value: "Data", position: "insideBottom", offset: -10 }}
            />
          ) : (
            <XAxis
              dataKey="index"
              type="number"
              domain={[1, data.length || 1]}
              ticks={tournamentTicks}
              tickFormatter={tournamentTickFormatter}
              label={{ value: "Numer turnieju", position: "insideBottom", offset: -10 }}
            />
          )}
          <YAxis
            label={{ value: "Rating", angle: -90, position: "insideLeft" }}
            domain={["dataMin - 50", "dataMax + 50"]}
          />
          <Tooltip
            labelFormatter={(value) =>
              xAxisMode === "date"
                ? formatDate(Number(value))
                : tournamentTickFormatter(Number(value))
            }
            formatter={(value) => [String(value ?? ""), "Rating"]}
          />
          <Line
            type="monotone"
            dataKey="rating"
            stroke="#ff7300"
            name="Rating"
            dot={{
              r: 3,
              cursor: "pointer",
              onClick: (_event: unknown, payload: unknown) => {
                const tn = (payload as { payload?: { tournamentNumber?: number | null } })
                  ?.payload?.tournamentNumber;
                if (typeof tn === "number" && onPointClick) {
                  onPointClick(tn);
                }
              },
            }}
            activeDot={{
              r: 6,
              cursor: "pointer",
              onClick: (_event: unknown, payload: unknown) => {
                const tn = (payload as { payload?: { tournamentNumber?: number | null } })
                  ?.payload?.tournamentNumber;
                if (typeof tn === "number" && onPointClick) {
                  onPointClick(tn);
                }
              },
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DoublesView;
